'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { SplaceWordmark } from '@/components/ui/Logo';
import { STEPS, type OnboardingData, type WizardCtx } from './types';
import { StepRouter } from './StepRouter';

const INITIAL_DATA: OnboardingData = {
  email: '', username: '', displayName: '', bio: '', photoUrl: null, notifAllowed: null,
};

const PROFILE_STEP_IDX = STEPS.findIndex(s => s.id === 'profile');

export function OnboardingWizard() {
  const searchParams = useSearchParams();
  const router = useRouter();

  // If the user already authenticated elsewhere (login OTP for an unknown
  // email, or Google sign-in for a brand new account), skip ahead to profile.
  const from = searchParams.get('from'); // 'login' | 'google' | null
  const skipToProfile = from === 'login' || from === 'google';
  const emailParam = searchParams.get('email') ?? '';

  const [stepIdx, setStepIdx] = useState(skipToProfile ? PROFILE_STEP_IDX : 0);
  const [data, setData] = useState<OnboardingData>({
    ...INITIAL_DATA,
    email: emailParam,
  });

  const steps = STEPS;
  const pct = Math.round((stepIdx / (steps.length - 1)) * 100);

  function back() {
    // If the user landed here already authenticated and is still at/before
    // profile, "back" should leave the wizard rather than rewind to a step
    // that no longer applies (signup/verify already happened).
    if (skipToProfile && stepIdx <= PROFILE_STEP_IDX) {
      router.push(from === 'google' ? '/feed' : '/login');
      return;
    }
    setStepIdx(i => Math.max(i - 1, 0));
  }

  const ctx: WizardCtx = {
    data,
    update: (patch) => setData(d => ({ ...d, ...patch })),
    next: () => setStepIdx(i => Math.min(i + 1, steps.length - 1)),
    back,
    stepIdx,
    steps,
  };

  // Group steps for sidebar
  const groups: { group: string; items: typeof steps[number][] & { idx: number }[] }[] = [];
  steps.forEach((s, i) => {
    const last = groups[groups.length - 1];
    const item = { ...s, idx: i };
    if (last && last.group === s.group) last.items.push(item as any);
    else groups.push({ group: s.group, items: [item as any] });
  });

  return (
    <div className="ob-root" data-theme="dark">
      {/* Sidebar */}
      <aside className="ob-side">
        <div className="ob-side-head">
          <Link href="/">
            <SplaceWordmark height={28} style={{ color: 'var(--text)' }} />
          </Link>
        </div>

        <nav className="ob-side-progress">
          {groups.map(g => (
            <div key={g.group} className="ob-side-group">
              <div className="ob-side-group-title">{g.group}</div>
              {g.items.map((s: any) => {
                const status = s.idx < stepIdx ? 'done' : s.idx === stepIdx ? 'current' : 'todo';
                return (
                  <div key={s.id} className={`ob-side-step ob-side-step--${status}`}>
                    <div className="ob-side-bullet">
                      {status === 'done' ? <CheckIcon /> : <span className="ob-side-num">{s.idx + 1}</span>}
                    </div>
                    <span className="ob-side-label">{s.title}</span>
                  </div>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="ob-side-foot">
          <span className="sp-mono" style={{ fontSize: 11, color: 'var(--text-dim)' }}>
            Step {stepIdx + 1} of {steps.length}
          </span>
          <a href="#" className="ob-side-help">Need help?</a>
        </div>
      </aside>

      {/* Main */}
      <main className="ob-main">
        {/* Top bar */}
        <div className="ob-top">
          <button className="ob-back" onClick={ctx.back} disabled={stepIdx === 0}>
            <ArrowLeftIcon /> Back
          </button>
          <div className="ob-top-progress">
            <div className="ob-top-progress-fill" style={{ width: `${pct}%` }} />
          </div>
          <span className="sp-mono" style={{ fontSize: 11, color: 'var(--text-dim)' }}>
            {String(stepIdx + 1).padStart(2, '0')} / {String(steps.length).padStart(2, '0')}
          </span>
        </div>

        {/* Step content */}
        <div className="ob-stage">
          <StepRouter ctx={ctx} />
        </div>
      </main>

      <style>{CSS}</style>
    </div>
  );
}

// ── Icons ──────────────────────────────────────────────────────
function CheckIcon() {
  return <svg width={11} height={11} viewBox="0 0 16 16" fill="none"><path d="M3 8.5L6.5 12L13 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>;
}
function ArrowLeftIcon() {
  return <svg width={14} height={14} viewBox="0 0 16 16" fill="none"><path d="M13 8H3M3 8L7 4M3 8L7 12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>;
}

// ── Wizard-specific CSS ────────────────────────────────────────
const CSS = `
.ob-root {
  display: flex; min-height: 100dvh;
  background: var(--bg); color: var(--text);
  font-family: var(--font-sans);
}

/* sidebar */
.ob-side {
  width: 260px; flex-shrink: 0;
  background: var(--bg-elev); border-right: 1px solid var(--line);
  display: flex; flex-direction: column;
  padding: 28px 20px; position: sticky; top: 0; height: 100dvh;
  overflow-y: auto;
}
.ob-side-head { margin-bottom: 32px; }
.ob-side-progress { flex: 1; display: flex; flex-direction: column; gap: 24px; }
.ob-side-group {}
.ob-side-group-title {
  font-family: var(--font-mono); font-size: 10px; font-weight: 600;
  letter-spacing: 0.12em; text-transform: uppercase; color: var(--text-dim);
  margin-bottom: 8px; padding-left: 4px;
}
.ob-side-step {
  display: flex; align-items: center; gap: 10px;
  padding: 6px 4px; border-radius: var(--r-xs);
}
.ob-side-bullet {
  width: 22px; height: 22px; border-radius: 50%;
  display: grid; place-items: center; flex-shrink: 0;
  font-family: var(--font-mono); font-size: 10px; font-weight: 600;
  border: 1.5px solid var(--line); color: var(--text-dim);
  transition: all 0.2s;
}
.ob-side-step--done .ob-side-bullet { background: var(--accent); border-color: var(--accent); color: white; }
.ob-side-step--current .ob-side-bullet { border-color: var(--accent); color: var(--accent); }
.ob-side-label { font-size: 13px; color: var(--text-dim); transition: color 0.2s; }
.ob-side-step--done .ob-side-label    { color: var(--text-muted); }
.ob-side-step--current .ob-side-label { color: var(--text); font-weight: 600; }
.ob-side-num {}
.ob-side-foot { margin-top: 24px; display: flex; flex-direction: column; gap: 8px; }
.ob-side-help { font-size: 12px; color: var(--accent); }

/* main */
.ob-main { flex: 1; display: flex; flex-direction: column; min-height: 100dvh; overflow-y: auto; }

.ob-top {
  display: flex; align-items: center; gap: 16px;
  padding: 16px 32px; border-bottom: 1px solid var(--line);
  position: sticky; top: 0; background: var(--bg); z-index: 1;
}
.ob-back {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 13px; font-weight: 500; color: var(--text-muted);
  background: none; border: none; cursor: pointer; padding: 0;
  transition: color 0.15s;
}
.ob-back:hover { color: var(--text); }
.ob-back:disabled { opacity: 0.3; pointer-events: none; }
.ob-top-progress {
  flex: 1; height: 3px; background: var(--line);
  border-radius: var(--r-pill); overflow: hidden;
}
.ob-top-progress-fill {
  height: 100%; background: var(--accent);
  border-radius: var(--r-pill); transition: width 0.4s var(--ease-out);
}

.ob-stage { flex: 1; display: flex; flex-direction: column; }

/* step layouts */
.ob-step { flex: 1; display: flex; }
.ob-step--hero {
  display: grid;
  grid-template-columns: 1fr 1fr;
}
.ob-step-body {
  padding: 48px 56px;
  display: flex; flex-direction: column;
  min-width: 0; width: 100%;
  max-width: 720px;
  margin-inline: auto;
}
.ob-step--hero > .ob-step-body { max-width: none; margin-inline: 0; }
.ob-step-body--narrow { max-width: 480px; }
.ob-google-wrap { display: flex; justify-content: center; width: 100%; overflow: hidden; }
.ob-google-wrap > * { max-width: 100%; }
.ob-hero {
  background: var(--bg-elev); border-left: 1px solid var(--line);
  display: flex; align-items: center; justify-content: center;
  padding: 40px;
}

.ob-eyebrow {
  font-family: var(--font-mono); font-size: 11px; font-weight: 600;
  letter-spacing: 0.12em; text-transform: uppercase; color: var(--text-dim);
  margin-bottom: 12px;
}
.ob-title { font-size: clamp(28px, 4.4vw, 36px); font-weight: 800; letter-spacing: -0.03em; line-height: 1.05; margin-bottom: 12px; }
.ob-sub { font-size: 15px; color: var(--text-muted); line-height: 1.6; margin-bottom: 32px; }
.ob-content { flex: 1; }
.ob-footer { margin-top: 32px; display: flex; align-items: center; gap: 12px; }
.ob-foot-link { font-size: 13px; color: var(--text-dim); }
.ob-foot-link a { color: var(--accent); }

/* form */
.ob-form { display: flex; flex-direction: column; gap: 14px; }
.ob-label {
  font-size: 12px; font-weight: 600; color: var(--text-muted);
  text-transform: uppercase; letter-spacing: 0.06em;
  display: block; margin-bottom: 6px;
}
.ob-input { display: block; }
.ob-textarea { min-height: 80px; resize: vertical; }
.ob-counter { font-size: 11px; color: var(--text-dim); text-align: right; margin-top: -8px; }
.ob-divider {
  display: flex; align-items: center; gap: 12px;
  color: var(--text-dim); font-size: 12px;
}
.ob-divider::before, .ob-divider::after {
  content: ''; flex: 1; height: 1px; background: var(--line);
}

/* buttons */
.ob-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  font-size: 14px; font-weight: 600; border-radius: var(--r-pill);
  padding: 11px 20px; transition: all 0.15s; border: none; cursor: pointer;
}
.ob-btn:disabled { opacity: 0.4; pointer-events: none; }
.ob-btn--primary { background: var(--accent); color: white; box-shadow: var(--shadow-accent); }
.ob-btn--primary:hover { background: var(--accent-hover); }
.ob-btn--primary:active { background: var(--accent-press); transform: scale(0.97); }
.ob-btn--ghost { background: transparent; color: var(--text); border: 1px solid var(--line); }
.ob-btn--ghost:hover { background: var(--bg-elev-2); }
.ob-btn--social {
  background: var(--bg-elev-2); color: var(--text); border: 1px solid var(--line);
  width: 100%; justify-content: flex-start; gap: 10px; padding: 11px 14px;
}
.ob-btn--social:hover { background: var(--bg-elev); border-color: var(--line-soft); }

.ob-link-btn { background: none; border: none; color: var(--accent); font-size: 13px; cursor: pointer; font: inherit; }
.ob-fineprint { font-size: 12px; color: var(--text-dim); line-height: 1.6; }
.ob-fineprint a { color: var(--accent); }
.ob-row { display: flex; align-items: center; gap: 10px; }

/* OTP */
.ob-otp { display: flex; gap: clamp(6px, 1.6vw, 8px); }
.ob-otp-cell {
  flex: 1; min-width: 0; max-width: 56px; aspect-ratio: 48/56;
  padding: 0; border-radius: var(--r-md);
  background: var(--bg-input); border: 1px solid var(--line);
  color: var(--text); font-size: 22px; font-weight: 700; font-family: var(--font-mono);
  text-align: center; outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.ob-otp-cell:focus { border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-soft); }
.ob-otp-meta { margin: 12px 0; }

/* Handle */
.ob-handle {
  display: flex; align-items: center;
  background: var(--bg-input); border: 1px solid var(--line);
  border-radius: var(--r-sm); overflow: hidden;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.ob-handle:focus-within { border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-soft); }
.ob-handle-prefix { padding: 12px 10px 12px 14px; color: var(--text-dim); font-size: 14px; white-space: nowrap; }
.ob-handle-input {
  flex: 1; background: none; border: none; outline: none;
  color: var(--text); font: inherit; font-size: 14px; padding: 12px 0;
}
.ob-handle-status { padding: 0 12px; }
.ob-status { display: inline-flex; align-items: center; gap: 4px; font-size: 12px; font-family: var(--font-mono); }
.ob-status--ok  { color: var(--tier-2); }
.ob-status--bad { color: var(--accent); }
.ob-status--soft { color: var(--text-dim); }

/* Chips */
.ob-suggestions { display: flex; gap: 8px; flex-wrap: wrap; }
.ob-chip {
  padding: 6px 12px; border-radius: var(--r-pill);
  background: var(--bg-elev-2); border: 1px solid var(--line);
  color: var(--text-muted); font-size: 13px; font-weight: 500; cursor: pointer;
  transition: all 0.15s;
}
.ob-chip:hover  { border-color: var(--accent); color: var(--text); }
.ob-chip--on    { background: var(--accent-soft); border-color: var(--accent); color: var(--accent); font-weight: 600; }

/* Profile grid */
.ob-profile-grid { display: grid; grid-template-columns: 1fr 200px; gap: 32px; }
.ob-avatar-side { display: flex; flex-direction: column; gap: 12px; }
.ob-avatar-picker {
  width: 160px; height: 160px; border-radius: 50%;
  background: var(--bg-elev-2); border: 2px dashed var(--line);
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 8px; color: var(--text-dim); font-size: 13px; cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
}
.ob-avatar-picker:hover { border-color: var(--accent); color: var(--accent); }
.ob-avatar-preview { width: 160px; height: 160px; border-radius: 50%; object-fit: cover; display: block; }
.ob-avatar-tip {
  display: flex; gap: 10px; padding: 12px;
  background: var(--bg-elev-2); border-radius: var(--r-md); border: 1px solid var(--line);
  font-size: 12px; color: var(--text-muted); line-height: 1.5;
}
.ob-avatar-tip strong { display: block; color: var(--text); margin-bottom: 2px; }
.ob-avatar-tip p { margin: 0; }

/* Choice (creator-q) */
.ob-choice-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 8px; }
.ob-choice {
  display: flex; flex-direction: column; gap: 8px; text-align: left;
  padding: 20px; border-radius: var(--r-lg);
  background: var(--bg-elev); border: 1.5px solid var(--line);
  cursor: pointer; transition: all 0.15s;
}
.ob-choice:hover { border-color: var(--accent); background: var(--accent-softer); }
.ob-choice--secondary:hover { border-color: var(--line-soft); background: var(--bg-elev-2); }
.ob-choice-emoji { font-size: 24px; }
.ob-choice-title { font-size: 15px; font-weight: 700; color: var(--text); }
.ob-choice-text  { font-size: 13px; color: var(--text-muted); line-height: 1.5; }
.ob-choice-meta  { font-size: 11px; color: var(--text-dim); font-family: var(--font-mono); margin-top: 4px; }

/* Welcome bullets */
.ob-welcome-bullets { display: flex; flex-direction: column; gap: 20px; }
.ob-bullet { display: flex; gap: 14px; align-items: flex-start; }
.ob-bullet-icon {
  width: 36px; height: 36px; border-radius: var(--r-md);
  background: var(--accent-softer); color: var(--accent);
  display: grid; place-items: center; flex-shrink: 0;
}
.ob-bullet-title { font-size: 14px; font-weight: 600; color: var(--text); margin-bottom: 2px; }
.ob-bullet-text  { font-size: 13px; color: var(--text-muted); line-height: 1.5; }

/* Hero stack (welcome) */
.ob-hero-stack { position: relative; width: 280px; height: 340px; }
.ob-hero-card {
  position: absolute; border-radius: var(--r-lg); overflow: hidden;
  border: 1px solid var(--line); box-shadow: var(--shadow-pop);
}
.ob-hero-card--1 { width: 180px; height: 220px; top: 0; left: 20px; z-index: 3; }
.ob-hero-card--2 { width: 160px; height: 200px; top: 60px; left: 100px; z-index: 2; transform: rotate(4deg); }
.ob-hero-card--3 { width: 140px; height: 180px; top: 120px; left: 10px; z-index: 1; transform: rotate(-3deg); }
.ob-hero-tag {
  position: absolute; bottom: 0; right: -20px;
  display: flex; align-items: center; gap: 8px; z-index: 4;
  background: var(--bg-elev); border: 1px solid var(--line);
  padding: 8px 14px; border-radius: var(--r-pill);
  font-size: 12px; font-weight: 600; color: var(--text); box-shadow: var(--shadow-pop);
}
.ob-hero-tag-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--tier-2); flex-shrink: 0; }

/* Earnings calculator */
.ob-calc {
  background: var(--bg-elev-2); border: 1px solid var(--line);
  border-radius: var(--r-xl); padding: 28px; width: 100%;
}
.ob-calc-head { margin-bottom: 16px; }
.ob-calc-figure { display: flex; align-items: baseline; gap: 4px; margin-bottom: 20px; }
.ob-calc-currency { font-size: 28px; font-weight: 700; color: var(--money); }
.ob-calc-amount   { font-size: 64px; font-weight: 800; letter-spacing: -0.04em; color: var(--money); line-height: 1; }
.ob-calc-period   { font-size: 14px; color: var(--text-muted); margin-left: 4px; }
.ob-calc-bar-wrap { margin-bottom: 20px; }
.ob-calc-slider {
  -webkit-appearance: none; appearance: none;
  width: 100%; height: 4px; border-radius: var(--r-pill);
  background: var(--line); outline: none; cursor: pointer;
}
.ob-calc-slider::-webkit-slider-thumb {
  -webkit-appearance: none; width: 20px; height: 20px; border-radius: 50%;
  background: var(--accent); cursor: pointer; box-shadow: var(--shadow-accent);
}
.ob-calc-scale {
  display: flex; justify-content: space-between;
  font-size: 11px; color: var(--text-dim); font-family: var(--font-mono);
  margin-top: 8px;
}
.ob-calc-rows { display: flex; flex-direction: column; gap: 8px; }
.ob-calc-row {
  display: flex; justify-content: space-between; align-items: center;
  font-size: 13px; color: var(--text-muted); padding: 8px 0;
  border-top: 1px solid var(--line-soft);
}
.ob-calc-row--fee   { color: var(--accent); }
.ob-calc-row--total { font-weight: 700; color: var(--text); }
.ob-calc-take       { color: var(--money); font-size: 15px; }

/* Earn list (creator-pitch) */
.ob-earn-list { display: flex; flex-direction: column; gap: 16px; }
.ob-earn-row  { display: flex; gap: 14px; align-items: flex-start; }
.ob-earn-icon {
  width: 36px; height: 36px; border-radius: var(--r-md);
  background: var(--bg-elev-2); color: var(--text-muted);
  display: grid; place-items: center; flex-shrink: 0;
}
.ob-earn-title { font-size: 14px; font-weight: 600; color: var(--text); margin-bottom: 2px; }
.ob-earn-text  { font-size: 13px; color: var(--text-muted); line-height: 1.5; }

/* Tier ladder */
.ob-ladder { display: flex; flex-direction: column; gap: 2px; margin-bottom: 24px; }
.ob-ladder-row {
  display: flex; align-items: center; gap: 14px;
  padding: 16px; border-radius: var(--r-md); border: 1px solid var(--line);
  background: var(--bg-elev); transition: border-color 0.15s;
}
.ob-ladder-row:hover { border-color: var(--line-soft); background: var(--bg-elev-2); }
.ob-ladder-num  { font-size: 11px; color: var(--text-dim); width: 20px; }
.ob-ladder-dot  { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
.ob-ladder-info { flex: 1; }
.ob-ladder-name { font-size: 14px; font-weight: 700; color: var(--text); display: flex; align-items: center; gap: 8px; }
.ob-ladder-range { font-size: 12px; color: var(--text-dim); margin-top: 2px; }
.ob-ladder-badge {
  font-size: 9px; padding: 2px 6px; border-radius: var(--r-pill);
  background: var(--accent-soft); color: var(--accent); font-weight: 700; letter-spacing: 0.06em;
}
.ob-ladder-fee { text-align: right; }
.ob-ladder-fee-num  { font-size: 16px; font-weight: 800; color: var(--text); }
.ob-ladder-fee-note { font-size: 11px; color: var(--text-dim); }
.ob-tiers-fineprint { display: flex; flex-direction: column; gap: 8px; }
.ob-fine-row { display: flex; align-items: flex-start; gap: 10px; font-size: 12px; color: var(--text-dim); }
.ob-fine-icon { flex-shrink: 0; margin-top: 1px; }

/* KYC */
.ob-kyc { display: flex; align-items: center; gap: 0; margin-bottom: 28px; }
.ob-kyc-step { display: flex; flex-direction: column; align-items: center; gap: 6px; }
.ob-kyc-step-num {
  width: 32px; height: 32px; border-radius: 50%;
  border: 1.5px solid var(--line);
  display: grid; place-items: center;
  font-size: 13px; font-weight: 600; color: var(--text-dim);
}
.ob-kyc-step--current .ob-kyc-step-num { border-color: var(--accent); color: var(--accent); }
.ob-kyc-step--done .ob-kyc-step-num    { background: var(--accent); border-color: var(--accent); color: white; }
.ob-kyc-step span { font-size: 11px; color: var(--text-dim); }
.ob-kyc-line { flex: 1; height: 1.5px; background: var(--line); margin: 0 8px; margin-bottom: 20px; }
.ob-kyc-line--done { background: var(--accent); }
.ob-kyc-panel { margin-bottom: 20px; }
.ob-kyc-doc-options { display: flex; gap: 10px; margin-bottom: 12px; }
.ob-kyc-doc {
  flex: 1; padding: 16px 12px; border-radius: var(--r-md);
  background: var(--bg-elev); border: 1.5px solid var(--line);
  display: flex; flex-direction: column; align-items: center; gap: 8px;
  font-size: 13px; color: var(--text-muted); cursor: pointer; transition: all 0.15s;
}
.ob-kyc-doc:hover { border-color: var(--accent); color: var(--text); }
.ob-kyc-doc-icon  { font-size: 22px; }
.ob-kyc-uploading { display: flex; flex-direction: column; gap: 12px; align-items: center; padding: 20px; }
.ob-kyc-progress  { width: 100%; height: 4px; background: var(--line); border-radius: var(--r-pill); overflow: hidden; }
.ob-kyc-progress-fill { width: 60%; height: 100%; background: var(--accent); border-radius: var(--r-pill); animation: ob-progress 1.2s ease-in-out infinite alternate; }
.ob-kyc-selfie    { width: 200px; height: 200px; border-radius: 50%; margin: 0 auto 16px; }
.ob-kyc-done      { display: flex; flex-direction: column; align-items: center; gap: 12px; padding: 24px; text-align: center; }
.ob-kyc-check {
  width: 52px; height: 52px; border-radius: 50%;
  background: oklch(40% 0.12 145 / 0.2); color: var(--tier-2);
  display: grid; place-items: center;
}

/* Payout */
.ob-payout { display: flex; flex-direction: column; gap: 8px; margin-bottom: 16px; }
.ob-payout-row {
  display: flex; align-items: center; gap: 14px; padding: 16px;
  border-radius: var(--r-md); background: var(--bg-elev); border: 1.5px solid var(--line);
  cursor: pointer; transition: all 0.15s; text-align: left; width: 100%;
}
.ob-payout-row:hover { border-color: var(--line-soft); background: var(--bg-elev-2); }
.ob-payout-row.is-active { border-color: var(--accent); background: var(--accent-softer); }
.ob-payout-mark {
  width: 40px; height: 40px; border-radius: var(--r-sm);
  background: var(--bg-elev-2); border: 1px solid var(--line);
  display: grid; place-items: center; font-size: 11px; font-weight: 700; flex-shrink: 0;
}
.ob-payout-info { flex: 1; }
.ob-payout-name  { font-size: 14px; font-weight: 600; color: var(--text); display: flex; align-items: center; gap: 8px; }
.ob-payout-blurb { font-size: 12px; color: var(--text-dim); margin-top: 2px; }
.ob-payout-tag {
  font-size: 9px; padding: 2px 6px; border-radius: var(--r-pill);
  background: var(--tier-2); color: #0E1116; font-weight: 700; opacity: 0.9;
}
.ob-payout-radio {
  width: 20px; height: 20px; border-radius: 50%; border: 1.5px solid var(--line);
  display: grid; place-items: center; flex-shrink: 0;
}
.ob-payout-row.is-active .ob-payout-radio { border-color: var(--accent); }
.ob-payout-radio-dot { width: 10px; height: 10px; border-radius: 50%; background: var(--accent); }
.ob-payout-foot { font-size: 12px; color: var(--text-dim); }

/* Interests + follow */
.ob-interests { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 28px; }
.ob-creators-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
.ob-creator-card { border-radius: var(--r-lg); overflow: hidden; background: var(--bg-elev); border: 1px solid var(--line); }
.ob-creator-cover { height: 80px; }
.ob-creator-avatar { width: 48px; height: 48px; border-radius: 50%; margin: -24px 0 0 12px; border: 2px solid var(--bg-elev); }
.ob-creator-meta { padding: 8px 12px 14px; }
.ob-creator-name { font-size: 13px; font-weight: 700; }
.ob-creator-handle { font-size: 11px; color: var(--text-dim); }
.ob-creator-bio  { font-size: 12px; color: var(--text-muted); line-height: 1.4; margin: 4px 0; }
.ob-creator-row  { display: flex; align-items: center; justify-content: space-between; }
.ob-creator-followers { font-size: 11px; color: var(--text-dim); }
.ob-follow-btn {
  padding: 4px 12px; border-radius: var(--r-pill); font-size: 12px; font-weight: 600;
  background: var(--accent); color: white; border: none; cursor: pointer; transition: all 0.15s;
}
.ob-follow-btn.is-on { background: var(--bg-elev-2); color: var(--text); border: 1px solid var(--line); }

/* Payment */
.ob-card-preview { display: flex; gap: 24px; align-items: flex-start; }
.ob-card-art {
  width: 200px; min-width: 200px; height: 120px; border-radius: var(--r-lg);
  background: linear-gradient(135deg, #1a1f2b, #2d1a2b);
  border: 1px solid rgba(255,255,255,.1);
  padding: 16px; display: flex; flex-direction: column; justify-content: space-between;
}
.ob-card-brand { font-size: 12px; font-weight: 700; color: rgba(255,255,255,.5); }
.ob-card-num   { font-size: 13px; color: rgba(255,255,255,.8); letter-spacing: 0.1em; }
.ob-card-foot  { display: flex; justify-content: space-between; font-size: 11px; color: rgba(255,255,255,.6); }

/* Notifications */
.ob-notif { display: flex; flex-direction: column; gap: 2px; margin-bottom: 20px; }
.ob-notif-row {
  display: flex; align-items: center; gap: 14px; padding: 14px;
  border-radius: var(--r-md); cursor: pointer; width: 100%;
  background: none; border: none; text-align: left;
  transition: background 0.15s;
}
.ob-notif-row:hover { background: var(--bg-elev-2); }
.ob-notif-label { font-size: 14px; font-weight: 500; color: var(--text); }
.ob-notif-sub   { font-size: 12px; color: var(--text-dim); margin-top: 2px; }
.ob-notif-channel { display: flex; align-items: center; gap: 12px; margin-bottom: 24px; }

/* Done */
.ob-done { text-align: center; padding: 24px 0; }
.ob-done-mark { margin: 0 auto 8px; width: fit-content; }
.ob-done-title { font-size: clamp(32px, 7vw, 52px); margin-top: 20px; }
.ob-done-sub { font-size: clamp(15px, 1.8vw, 17px); }
.ob-done-summary { display: flex; flex-direction: column; gap: 0; margin-top: 28px; border: 1px solid var(--line); border-radius: var(--r-lg); overflow: hidden; }
.ob-summary-row { display: flex; justify-content: space-between; align-items: center; padding: 14px 18px; border-bottom: 1px solid var(--line-soft); font-size: 14px; }
.ob-summary-row:last-child { border-bottom: none; }
.ob-summary-label { color: var(--text-muted); }
.ob-summary-value { color: var(--text); font-weight: 600; }

/* Spinner */
.ob-spinner { animation: ob-spin 0.8s linear infinite; }

@keyframes ob-progress { from { transform: translateX(-100%); } to { transform: translateX(100%); } }

@media (max-width: 1100px) {
  .ob-step-body { padding: 36px 32px; }
}

@media (max-width: 900px) {
  .ob-step--hero { grid-template-columns: 1fr; }
  .ob-hero { display: none; }
  .ob-step-body { padding: 32px 24px; }
}

@media (max-width: 768px) {
  .ob-side { display: none; }
  .ob-top { padding: 14px 20px; }
  .ob-step--hero { grid-template-columns: 1fr; }
  .ob-step-body { padding: 28px 20px; }
  .ob-step-body--narrow { margin: 0 auto; }
  .ob-profile-grid { grid-template-columns: 1fr; gap: 24px; }
  .ob-profile-grid > .ob-avatar-side { order: -1; align-items: center; text-align: center; }
  .ob-avatar-picker, .ob-avatar-preview { width: 120px; height: 120px; }
  .ob-avatar-tip { text-align: left; }
  .ob-creators-grid { grid-template-columns: repeat(2, 1fr); }
  .ob-choice-grid { grid-template-columns: 1fr; }
  .ob-hero { display: none; }
  .ob-card-preview { flex-direction: column; }
  .ob-row { flex-wrap: wrap; }
  .ob-btn { padding: 11px 16px; }
}
`;
